import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/AppLayout.js';
import { SettingsPage } from './routes/settings.js';
import { AnalyticsPage } from './routes/analytics.js';
import { MemoryPage } from './routes/memory.js';
import { PullsPage } from './routes/pulls.js';
import { RepositoriesPage } from './routes/repositories.js';
import { DebugSentryPage } from './routes/debug-sentry.js';
import CodeReviewWhen from './routes/code-review/when.js';
import CodeReviewSummaries from './routes/code-review/summaries.js';
import CodeReviewInstructions from './routes/code-review/instructions.js';
import CodeReviewComments from './routes/code-review/comments.js';
import CodeReviewFilters from './routes/code-review/filters.js';
import CodeReviewAutoApprove from './routes/code-review/auto-approve.js';
import CodeReviewAgents from './routes/code-review/agents.js';
import CodeReviewStatusChecks from './routes/code-review/status-checks.js';
import { initSentry, Sentry, isSentryEnabled } from './sentry.js';
import './styles.css';

// Initialize BEFORE createRoot so the first render is captured if it
// throws. No-op when VITE_SENTRY_DSN is unset (see ./sentry.ts).
initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element missing');

/** App tree. Defined as a component (not an element) so the Sentry
 *  withErrorBoundary wrapper and the no-op fallback return the same shape. */
function App() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/settings" replace />} />
            <Route
              path="/settings"
              element={
                <AppLayout>
                  <SettingsPage />
                </AppLayout>
              }
            />
            <Route
              path="/repositories"
              element={
                <AppLayout>
                  <RepositoriesPage />
                </AppLayout>
              }
            />
            <Route
              path="/analytics"
              element={
                <AppLayout>
                  <AnalyticsPage />
                </AppLayout>
              }
            />
            <Route
              path="/memory"
              element={
                <AppLayout>
                  <MemoryPage />
                </AppLayout>
              }
            />
            <Route
              path="/pulls"
              element={
                <AppLayout>
                  <PullsPage />
                </AppLayout>
              }
            />
            <Route
              path="/debug-sentry"
              element={
                <AppLayout>
                  <DebugSentryPage />
                </AppLayout>
              }
            />
            <Route
              path="/code-review"
              element={<Navigate to="/code-review/when" replace />}
            />
            <Route
              path="/code-review/when"
              element={
                <AppLayout title="Code Review">
                  <CodeReviewWhen />
                </AppLayout>
              }
            />
            <Route
              path="/code-review/summaries"
              element={
                <AppLayout title="Code Review">
                  <CodeReviewSummaries />
                </AppLayout>
              }
            />
            <Route
              path="/code-review/instructions"
              element={
                <AppLayout title="Code Review">
                  <CodeReviewInstructions />
                </AppLayout>
              }
            />
            <Route
              path="/code-review/comments"
              element={
                <AppLayout title="Code Review">
                  <CodeReviewComments />
                </AppLayout>
              }
            />
            <Route
              path="/code-review/filters"
              element={
                <AppLayout title="Code Review">
                  <CodeReviewFilters />
                </AppLayout>
              }
            />
            <Route
              path="/code-review/auto-approve"
              element={
                <AppLayout title="Code Review">
                  <CodeReviewAutoApprove />
                </AppLayout>
              }
            />
            <Route
              path="/code-review/agents"
              element={
                <AppLayout title="Code Review">
                  <CodeReviewAgents />
                </AppLayout>
              }
            />
            <Route
              path="/code-review/status-checks"
              element={
                <AppLayout title="Code Review">
                  <CodeReviewStatusChecks />
                </AppLayout>
              }
            />
            <Route path="*" element={<Navigate to="/settings" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>
  );
}

// ErrorBoundary only wraps when Sentry is enabled. When disabled the
// fallback is rendered as the app root, so the SPA works exactly as
// before with zero overhead.
const Root = isSentryEnabled()
  ? Sentry.withErrorBoundary(App, {
      fallback: <div role="alert">Something went wrong. The error was reported.</div>,
    })
  : App;

createRoot(rootEl).render(<Root />);
